import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- Auto-refresh de session ----
// Sans interceptor, un access_token expiré (>1h) éjecte l'utilisateur alors que
// le refresh_token est valide 7 jours. On tente un /auth/refresh silencieux
// avant d'abandonner. Une seule tentative par requête pour éviter les boucles.
let refreshInflight = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    // On ne rejoue jamais /auth/refresh, /auth/login, /auth/register, /auth/logout
    const skip = /\/auth\/(refresh|login|register|logout)/.test(url);
    if (status === 401 && original && !original._retried && !skip) {
      original._retried = true;
      try {
        refreshInflight = refreshInflight || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await refreshInflight;
      } catch { /* refresh échoué → on laisse le 401 remonter */ }
      finally { refreshInflight = null; }
      try { return await api(original); } catch (e) { return Promise.reject(e); }
    }
    return Promise.reject(error);
  },
);

// Traduction de secours pour les messages Pydantic anglais (au cas où le
// handler backend ne serait pas appliqué). Le backend renvoie normalement
// déjà un `detail` en français via son exception handler.
const EN_FR_PATTERNS = [
  [/^value is not a valid email address.*/i, "Adresse e-mail invalide."],
  [/^field required$/i, "Champ obligatoire."],
  [/^string should have at least (\d+) characters?.*/i, (m) => `Trop court : ${m[1]} caractère(s) minimum.`],
  [/^string should have at most (\d+) characters?.*/i, (m) => `Trop long : ${m[1]} caractère(s) maximum.`],
  [/^input should be a valid.*/i, "Format invalide."],
  [/^ensure this value has at least (\d+).*/i, (m) => `Minimum ${m[1]} caractère(s).`],
  [/^ensure this value has at most (\d+).*/i, (m) => `Maximum ${m[1]} caractère(s).`],
  [/^network error$/i, "Erreur réseau. Vérifiez votre connexion."],
  [/^timeout of \d+ms exceeded$/i, "Délai dépassé. Réessayez."],
  [/^request failed with status code.*/i, "Erreur serveur. Réessayez."],
];

function translateEN(text) {
  if (typeof text !== "string") return text;
  for (const [re, repl] of EN_FR_PATTERNS) {
    const m = text.match(re);
    if (m) return typeof repl === "function" ? repl(m) : repl;
  }
  return text;
}

export function fmtError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return translateEN(err?.message || "Une erreur est survenue.");
  if (typeof d === "string") return translateEN(d);
  if (Array.isArray(d)) return d.map(e => translateEN(e?.msg || JSON.stringify(e))).join(" ");
  if (typeof d === "object" && d.msg) return translateEN(d.msg);
  if (typeof d === "object" && d.message) {
    const acts = Array.isArray(d.actions) ? d.actions : [];
    return translateEN(d.message) + (acts.length ? " " + acts.join(" ") : "");
  }
  return String(d);
}

// Formatage dédié 429 sur /api/files/* (Chantier 1) — extrait retry_at et
// construit un message convivial. Renvoie `null` si ce n'est pas un 429
// quota → l'appelant peut alors basculer sur fmtError.
export function fmt429DocsQuota(err) {
  if (err?.response?.status !== 429) return null;
  const d = err?.response?.data?.detail;
  if (!d || typeof d !== "object" || d.code !== "doc_access_quota_exceeded") return null;
  const retry = d.retry_at ? new Date(d.retry_at) : null;
  const now = new Date();
  let quand = "";
  if (retry && !isNaN(retry.getTime())) {
    const diffMin = Math.max(1, Math.round((retry - now) / 60000));
    if (diffMin < 60) quand = `dans ${diffMin} min`;
    else if (diffMin < 60 * 24) quand = `dans ${Math.round(diffMin / 60)} h ${diffMin % 60} min`;
    else quand = `le ${retry.toLocaleDateString("fr-FR")} à ${retry.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  }
  return {
    title: "Plafond quotidien atteint",
    message: `Vous avez consulté ${d.used || d.limit || 20} dossiers différents sur les dernières 24 h (limite ${d.limit || 20}).`,
    detail: quand
      ? `Nouveau dossier accessible ${quand}. Les dossiers déjà consultés dans les 24 h restent ouvrables.`
      : (d.help || "Réessayez plus tard."),
    retry_at: d.retry_at,
    retry_after_seconds: d.retry_after_seconds,
  };
}

// Ouvre un document administratif signé en passant par un fetch blob :
// - permet d'attraper le 429 du plafond (sinon window.open affiche le JSON brut)
// - construit une URL blob locale et la déclenche en téléchargement/onglet
// - libère l'URL blob 1 min plus tard pour ne pas fuiter la mémoire
export async function openSignedFile(signedUrl, { onQuota } = {}) {
  try {
    // Le backend renvoie une URL absolue commençant par `/api/...`. L'instance
    // `api` (baseURL = `${BASE}/api`) préfixe déjà `/api` — on retire donc le
    // préfixe pour éviter un `/api/api/...` (404).
    const relUrl = signedUrl.startsWith("/api/") ? signedUrl.slice(4) : signedUrl;
    const resp = await api.get(relUrl, { responseType: "blob" });
    const cd = resp.headers?.["content-disposition"] || "";
    const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
    const filename = m ? m[1] : "document.pdf";
    const url = URL.createObjectURL(resp.data);
    // Ouvre dans un nouvel onglet. Le PDF reste inline grâce au
    // Content-Disposition; inline; du serveur (visible en preview).
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      // Bloqueur de pop-up → fallback download
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch (err) {
    const q = fmt429DocsQuota(err);
    if (q && typeof onQuota === "function") { onQuota(q); return false; }
    throw err;
  }
}

export const BACKEND_URL = BASE;
